import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { OutreachDraft } from "@/components/atomic-crm/types";

const statusColors: Record<string, string> = {
    draft: "bg-gray-100 text-gray-800",
    approved: "bg-blue-100 text-blue-800",
    sent: "bg-green-100 text-green-800",
};

export function OutreachHistory({ contactId }: { contactId: number }) {
    const [drafts, setDrafts] = useState<OutreachDraft[]>([]);
    const [expanded, setExpanded] = useState<number | null>(null);

    useEffect(() => {
        supabase
            .from("outreach_drafts")
            .select("*")
            .eq("contact_id", contactId)
            .order("created_at", { ascending: false })
            .then(({ data }) => {
                if (data) setDrafts(data);
            });
    }, [contactId]);

    if (drafts.length === 0) return null;

    const handleMarkSent = async (id: number) => {
        await supabase
            .from("outreach_drafts")
            .update({ status: "sent", sent_at: new Date().toISOString() })
            .eq("id", id);
        setDrafts((prev) =>
            prev.map((d) =>
                d.id === id
                    ? { ...d, status: "sent" as const, sent_at: new Date().toISOString() }
                    : d,
            ),
        );
    };

    return (
        <div>
            <h3 className="mb-2 text-sm font-semibold">Outreach History</h3>
            <div className="space-y-2">
                {drafts.map((d) => (
                    <div key={d.id} className="rounded-md border p-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                    {d.channel}
                                </Badge>
                                <Badge
                                    className={`text-xs ${statusColors[d.status]}`}
                                >
                                    {d.status}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                    {new Date(d.created_at).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="flex gap-1">
                                {d.status === "approved" && (
                                    <button
                                        className="text-xs text-blue-600 hover:underline"
                                        onClick={() => handleMarkSent(d.id)}
                                    >
                                        Mark Sent
                                    </button>
                                )}
                                <button
                                    className="text-xs text-muted-foreground hover:underline"
                                    onClick={() =>
                                        setExpanded(
                                            expanded === d.id ? null : d.id,
                                        )
                                    }
                                >
                                    {expanded === d.id ? "Collapse" : "Expand"}
                                </button>
                            </div>
                        </div>
                        {expanded === d.id && (
                            <p className="mt-2 whitespace-pre-wrap text-sm">
                                {d.draft_content}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
